import React, { useState } from 'react';
import { Card, Badge, Image, Spinner, Button, Modal, Form, InputGroup } from 'react-bootstrap';
import { Package, Search, X } from 'lucide-react';
import type { SkinSeoulAdminProduct } from '../../../../models/ProductTypes';
import { PublicationStatus, StockStatus, SkinSeoulProductType } from '../../../../models/ProductTypes';
import { getConfig } from '../../../../config';
import {
  getPublicationStatusColor,
  getStockStatusColor,
  getProductTypeColor,
  getWaveColor,
} from '../../../../utils/statusUtils';
import { IdBadge, IdBadgeType } from '../../../../components/ui/badge/IdBadge';

interface SkinSeoulProductsTableProps {
  products: SkinSeoulAdminProduct[];
  loading: boolean;
  skuSearchValue?: string;
  onSkuSearch?: (sku: string) => void;
  onClearSkuSearch?: () => void;
  skuSearchLoading?: boolean;
}

// Helper function to get WordPress admin URL based on environment
const getWpAdminUrl = (): string => {
  const config = getConfig();
  return config.wpAdminUrl;
};

export const SkinSeoulProductsTable: React.FC<SkinSeoulProductsTableProps> = ({
  products,
  loading,
  skuSearchValue = '',
  onSkuSearch,
  onClearSkuSearch,
  skuSearchLoading = false,
}) => {
  const [selectedProduct, setSelectedProduct] = useState<SkinSeoulAdminProduct | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [localSkuInput, setLocalSkuInput] = useState(skuSearchValue);

  const openImageModal = (product: SkinSeoulAdminProduct) => {
    setSelectedProduct(product);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedProduct(null);
  };

  const handleSkuSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (localSkuInput.trim() && onSkuSearch) {
      onSkuSearch(localSkuInput.trim());
    }
  };

  const handleClearSearch = () => {
    setLocalSkuInput('');
    if (onClearSkuSearch) {
      onClearSkuSearch();
    }
  };

  const handleSkuInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSkuInput(e.target.value);
  };

  if (loading) {
    return (
      <Card className="h-100">
        <Card.Body className="text-center py-5 d-flex align-items-center justify-content-center">
          <div>
            <Spinner animation="border" variant="primary" />
            <div className="mt-3">Loading SkinSeoul products...</div>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="h-100">
        <Card.Header style={{ backgroundColor: '#000', color: '#fff' }}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <img
                src="https://skin-seoul.com/wp-content/uploads/2024/12/desktop-logo.png"
                alt="SkinSeoul"
                style={{ height: '34px', width: 'auto', margin: '1px' }}
              />

              {/* SKU Search */}
              <Form onSubmit={handleSkuSearch} style={{ minWidth: '300px' }}>
                <InputGroup size="sm">
                  <Form.Control
                    type="text"
                    placeholder="Search by SKU (e.g., 100014654)"
                    value={localSkuInput}
                    onChange={handleSkuInputChange}
                    disabled={skuSearchLoading}
                    style={{
                      backgroundColor: '#333',
                      borderColor: '#555',
                      color: '#fff',
                      fontSize: '0.85rem',
                    }}
                  />
                  {localSkuInput && (
                    <Button
                      variant="outline-secondary"
                      onClick={handleClearSearch}
                      disabled={skuSearchLoading}
                      style={{ borderColor: '#555', color: '#ccc' }}
                    >
                      <X size={14} />
                    </Button>
                  )}
                  <Button
                    variant="outline-primary"
                    type="submit"
                    disabled={!localSkuInput.trim() || skuSearchLoading}
                    style={{ borderColor: '#007bff', color: '#007bff' }}
                  >
                    {skuSearchLoading ? <Spinner animation="border" size="sm" /> : <Search size={14} />}
                  </Button>
                </InputGroup>
              </Form>
            </div>

            <div className="ms-auto">
              <div className="text-end">
                <small style={{ color: '#ccc' }}>
                  {products.length} result{products.length !== 1 ? 's' : ''}
                </small>
              </div>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-1" style={{ height: 'calc(100vh - 120px)', overflowY: 'auto', overflowX: 'hidden' }}>
          <div className="h-100" style={{ position: 'relative' }}>
            {products.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-3">
                  <Package size={48} className="text-muted" />
                </div>
                <div>
                  <h5>No SkinSeoul Products</h5>
                  <p>
                    {skuSearchValue
                      ? `No products found for SKU: ${skuSearchValue}`
                      : 'Click on a Glowpick product or search by SKU to view SkinSeoul products.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-1">
                {products
                  .sort((a, b) => a.sku.localeCompare(b.sku))
                  .map((product, index) => (
                    <React.Fragment key={product.productId}>
                      {/* Main Product */}
                      <Card
                        className="mb-3 shadow-sm"
                        style={{ overflow: 'hidden', maxWidth: '100%', boxSizing: 'border-box' }}
                      >
                        <Card.Body className="p-2" style={{ position: 'relative', overflow: 'hidden' }}>
                          <div className="d-flex" style={{ width: '100%', overflow: 'hidden' }}>
                            {/* Number at very left */}
                            <div className="me-2 flex-shrink-0 d-flex align-items-start">
                              <span
                                style={{
                                  fontSize: '0.8rem',
                                  fontWeight: 'bold',
                                  marginTop: '2px',
                                  color: '#6c757d',
                                }}
                              >
                                {index + 1}
                              </span>
                            </div>

                            {/* Product Image Gallery */}
                            <div className="me-3 flex-shrink-0">
                              <div className="position-relative">
                                <a
                                  href={product.productLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-decoration-none"
                                >
                                  {/* Main Image */}
                                  {product.productImageUrls && product.productImageUrls.length > 0 ? (
                                    <Image
                                      src={product.productImageUrls[0]}
                                      alt={product.name}
                                      width={120}
                                      height={120}
                                      className="rounded"
                                      style={{ objectFit: 'cover' }}
                                      onError={(e) => {
                                        const target = e.currentTarget as HTMLImageElement;
                                        target.src = 'https://via.placeholder.com/120x120?text=No+Image';
                                      }}
                                    />
                                  ) : (
                                    <div
                                      className="rounded d-flex align-items-center justify-content-center"
                                      style={{
                                        width: '120px',
                                        height: '120px',
                                        backgroundColor: '#f8f9fa',
                                        border: '1px solid #dee2e6',
                                      }}
                                    >
                                      <Package size={24} className="text-muted" />
                                    </div>
                                  )}
                                </a>

                                {/* Thumbnail Gallery */}
                                {product.productImageUrls && product.productImageUrls.length > 1 && (
                                  <div
                                    className="mt-2"
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(3, 38px)',
                                      gridTemplateRows: 'repeat(2, 38px)',
                                      gap: '2px',
                                    }}
                                  >
                                    {(() => {
                                      // Get unique images
                                      const uniqueImages = Array.from(new Set(product.productImageUrls));
                                      const displayImages = uniqueImages.slice(1, 6); // Skip first (main) image, show next 5
                                      const remainingCount = Math.max(0, uniqueImages.length - 6);

                                      return (
                                        <>
                                          {displayImages.map((url, thumbIndex) => (
                                            <Image
                                              key={thumbIndex}
                                              src={url}
                                              alt={`${product.name} ${thumbIndex + 2}`}
                                              width={38}
                                              height={38}
                                              className="rounded"
                                              style={{
                                                objectFit: 'cover',
                                                cursor: 'pointer',
                                                border: '1px solid #dee2e6',
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openImageModal(product);
                                              }}
                                              onError={(e) => {
                                                const target = e.currentTarget as HTMLImageElement;
                                                target.src = 'https://via.placeholder.com/38x38?text=?';
                                              }}
                                            />
                                          ))}

                                          {/* Show +X for remaining images */}
                                          {remainingCount > 0 && displayImages.length === 5 && (
                                            <div
                                              className="rounded d-flex align-items-center justify-content-center text-white"
                                              style={{
                                                width: '38px',
                                                height: '38px',
                                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                fontSize: '0.6rem',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                border: '1px solid #dee2e6',
                                              }}
                                              title={`${remainingCount} more images - Click to view all`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openImageModal(product);
                                              }}
                                            >
                                              +{remainingCount}
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Product Details */}
                            <div className="flex-grow-1" style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
                              <div className="d-flex" style={{ width: '100%', overflow: 'hidden' }}>
                                {/* Main content */}
                                <div className="flex-grow-1" style={{ minWidth: 0, overflow: 'hidden' }}>
                                  {/* Header with badges and action buttons */}
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div className="d-flex gap-2 flex-wrap">
                                      <Badge
                                        bg={getProductTypeColor(product.type || SkinSeoulProductType.Simple)}
                                        style={{ fontSize: '0.7rem' }}
                                      >
                                        {product.type === SkinSeoulProductType.Variable
                                          ? 'Variable'
                                          : product.type === SkinSeoulProductType.Variation
                                            ? 'Variation'
                                            : 'Simple'}
                                      </Badge>
                                      <Badge
                                        bg={getPublicationStatusColor(product.postStatus!)}
                                        style={{ fontSize: '0.7rem' }}
                                      >
                                        {product.postStatus || PublicationStatus.DRAFT}
                                      </Badge>
                                      <Badge bg={getStockStatusColor(product.status!)} style={{ fontSize: '0.7rem' }}>
                                        {product.status === StockStatus.OUT_OF_STOCK
                                          ? 'Out of Stock'
                                          : product.status === StockStatus.IN_STOCK
                                            ? 'In Stock'
                                            : product.status === StockStatus.ON_BACKORDER
                                              ? 'On Backorder'
                                              : product.status || 'Out of Stock'}
                                      </Badge>
                                      {product.attributes?.['wave'] && (
                                        <span
                                          className="badge"
                                          style={{
                                            fontSize: '0.7rem',
                                            ...getWaveColor(product.attributes['wave']),
                                          }}
                                        >
                                          W{product.attributes['wave']}
                                        </span>
                                      )}
                                    </div>

                                    {/* Action buttons */}
                                    <div className="d-flex gap-2">
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() =>
                                          window.open(
                                            `${getWpAdminUrl()}post.php?post=${product.productId}&action=edit`,
                                            '_blank',
                                          )
                                        }
                                        title="Edit in WordPress Admin"
                                      >
                                        WP Edit
                                      </Button>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => window.open(`/catalog/ss-editor?sku=${product.sku}`, '_blank')}
                                        title="Open in SS Editor"
                                      >
                                        SS Editor
                                      </Button>
                                    </div>
                                  </div>

                                  {/* SKU and CP Info */}
                                  <div className="d-flex align-items-center gap-3 mb-2">
                                    <div className="d-flex align-items-center">
                                      <IdBadge type={IdBadgeType.SKU} value={product.sku} size="sm" />
                                    </div>

                                    {product.attributes?.['cp_parent_code'] && (
                                      <div className="d-flex align-items-center gap-2">
                                        <IdBadge
                                          type={IdBadgeType.CPPI}
                                          value={product.attributes['cp_parent_code'] || ''}
                                          size="sm"
                                        />
                                        <IdBadge
                                          type={IdBadgeType.CPII}
                                          value={product.attributes['cp_child_code'] || ''}
                                          size="sm"
                                        />
                                        {product.attributes?.['cp_url'] && (
                                          <a
                                            href={product.attributes['cp_url']}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-decoration-none"
                                            style={{ fontSize: '0.8rem' }}
                                          >
                                            View on Coupang
                                          </a>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {/* Category */}
                                  <div className="mb-2">
                                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                      {product.category.join(' › ')}
                                    </div>
                                  </div>

                                  {/* Product Name */}
                                  <h6
                                    className="fw-bold mb-2"
                                    style={{
                                      fontSize: '1rem',
                                      lineHeight: '1.2',
                                      wordBreak: 'break-word',
                                      overflowWrap: 'break-word',
                                    }}
                                  >
                                    {product.name}
                                  </h6>

                                  {product.nameKor && (
                                    <div className="mb-2 text-muted" style={{ fontSize: '0.85rem' }}>
                                      {product.nameKor}
                                    </div>
                                  )}

                                  {/* Product Link */}
                                  {product.productLink && (
                                    <div className="mb-2">
                                      <small
                                        className="text-muted"
                                        style={{ fontSize: '0.7rem', wordBreak: 'break-all' }}
                                      >
                                        <strong>Link:</strong>{' '}
                                        <a
                                          href={product.productLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary text-decoration-none"
                                          style={{ fontSize: '0.7rem' }}
                                        >
                                          {product.productLink}
                                        </a>
                                      </small>
                                    </div>
                                  )}

                                  {/* Size, Pack, Color */}
                                  <div className="d-flex gap-2 mb-2 flex-wrap">
                                    {product['size'] && (
                                      <div className="d-flex align-items-center">
                                        <small className="text-muted me-1">Size:</small>
                                        <small>{product['size']}</small>
                                      </div>
                                    )}
                                    {product['pack'] && (
                                      <div className="d-flex align-items-center">
                                        <small className="text-muted me-1">Pack:</small>
                                        <small>{product['pack']}</small>
                                      </div>
                                    )}
                                    {product.attributes?.['color'] && (
                                      <div className="d-flex align-items-center">
                                        <small className="text-muted me-1">Color:</small>
                                        <small>{product.attributes['color']}</small>
                                      </div>
                                    )}
                                  </div>

                                  {/* Price Information */}
                                  {(product.originalPrice || product.salePrice) && (
                                    <div className="d-flex align-items-center gap-2">
                                      <div className="d-flex align-items-center gap-2">
                                        {product.salePrice ? (
                                          <>
                                            <span className="text-danger me-2">
                                              {product.currency || 'Unknown'} {product.salePrice.toFixed(2)}
                                            </span>
                                            {product.originalPrice && (
                                              <span
                                                className="text-muted text-decoration-line-through me-2"
                                                style={{ fontSize: '0.8rem' }}
                                              >
                                                {product.currency || 'Unknown'} {product.originalPrice.toFixed(2)}
                                              </span>
                                            )}
                                            {product.originalPrice && (
                                              <span
                                                className="badge bg-danger text-white"
                                                style={{ fontSize: '0.65rem' }}
                                              >
                                                {Math.round(
                                                  ((product.originalPrice - product.salePrice) /
                                                    product.originalPrice) *
                                                    100,
                                                )}
                                                % OFF
                                              </span>
                                            )}
                                          </>
                                        ) : (
                                          // No sale price - show original price as regular price
                                          product.originalPrice && (
                                            <span className="text-dark" style={{ fontSize: '0.8rem' }}>
                                              {product.currency || 'SGD'} {product.originalPrice.toFixed(2)}
                                            </span>
                                          )
                                        )}
                                      </div>
                                      {product.attributes?.['total_sales'] && (
                                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                                          ({product.attributes['total_sales']} sold)
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  {/* Variations count if applicable */}
                                  {product.type === SkinSeoulProductType.Variable &&
                                    product.variations &&
                                    product.variations.length > 0 && (
                                      <div>
                                        <small className="text-muted">
                                          {product.variations.length} variation
                                          {product.variations.length !== 1 ? 's' : ''}
                                        </small>
                                      </div>
                                    )}
                                </div>

                                {/* PDP Images - Bottom Right (Absolute Position) */}
                                {(product.learnMoreUrl || product.attributes?.['learn_more_url']) && (
                                  <div
                                    className="position-absolute d-flex gap-1"
                                    style={{
                                      bottom: '16px',
                                      right: '8px',
                                      zIndex: 1,
                                      maxWidth: '120px',
                                      overflow: 'hidden',
                                      flexShrink: 0,
                                    }}
                                  >
                                    {(() => {
                                      // Handle both learnMoreUrl array and learn_more_url string
                                      let urls: string[] = [];

                                      if (product.learnMoreUrl && Array.isArray(product.learnMoreUrl)) {
                                        urls = product.learnMoreUrl;
                                      } else if (product.attributes?.['learn_more_url']) {
                                        if (typeof product.attributes['learn_more_url'] === 'string') {
                                          urls = product.attributes['learn_more_url']
                                            .split(',')
                                            .map((url) => url.trim());
                                        } else if (Array.isArray(product.attributes['learn_more_url'])) {
                                          urls = product.attributes['learn_more_url'];
                                        }
                                      }

                                      const displayUrls = urls.slice(0, 2);
                                      const remainingCount = urls.length - 2;

                                      return (
                                        <>
                                          {displayUrls.map((url: string, index: number) => (
                                            <div key={index} className="position-relative">
                                              <Image
                                                src={url}
                                                alt={`Learn More ${index + 1}`}
                                                width={60}
                                                height={100}
                                                className="rounded"
                                                style={{
                                                  objectFit: 'cover',
                                                  cursor: 'pointer',
                                                  border: '1px solid #dee2e6',
                                                }}
                                                onClick={() => window.open(url, '_blank')}
                                                onError={(e) => {
                                                  const target = e.currentTarget as HTMLImageElement;
                                                  target.src = 'https://via.placeholder.com/60x100?text=PDP';
                                                }}
                                              />
                                              <div
                                                className="position-absolute bottom-0 start-0 px-1 text-white"
                                                style={{
                                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                  fontSize: '0.45rem',
                                                  borderRadius: '0 2px 0 0',
                                                }}
                                              >
                                                PDP {index + 1}
                                              </div>
                                            </div>
                                          ))}

                                          {/* Show more button if there are additional PDP images */}
                                          {remainingCount > 0 && (
                                            <div
                                              className="rounded d-flex align-items-center justify-content-center text-white"
                                              style={{
                                                width: '50px',
                                                height: '85px',
                                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                                fontSize: '0.65rem',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                border: '1px solid #dee2e6',
                                              }}
                                              title={`${remainingCount} more PDP images - Click to view all`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openImageModal(product);
                                              }}
                                            >
                                              +{remainingCount}
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>

                      {/* Variations for this Variable product */}
                      {product.type === SkinSeoulProductType.Variable &&
                        product.variations &&
                        product.variations.length > 0 &&
                        product.variations
                          .sort((a, b) => a.sku.localeCompare(b.sku))
                          .map((variation, variationIndex) => (
                            <Card
                              key={`${product.productId}-var-${variation.productId || variationIndex}`}
                              className="mb-3 shadow-sm ms-4"
                              style={{
                                backgroundColor: '#f8f9fa',
                                marginLeft: '2rem',
                                overflow: 'hidden',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                              }}
                            >
                              <Card.Body className="p-2" style={{ position: 'relative', overflow: 'hidden' }}>
                                <div className="d-flex" style={{ width: '100%', overflow: 'hidden' }}>
                                  {/* Hierarchy indicator and numbering at very left */}
                                  <div className="me-2 flex-shrink-0 d-flex align-items-start">
                                    <small
                                      className="text-muted me-1"
                                      style={{
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold',
                                        marginTop: '2px',
                                      }}
                                    >
                                      ↳
                                    </small>
                                    <span
                                      style={{
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold',
                                        marginTop: '2px',
                                        color: '#6c757d',
                                      }}
                                    >
                                      {index + 1}.{variationIndex + 1}
                                    </span>
                                  </div>

                                  {/* Variation Image */}
                                  <div className="me-3 flex-shrink-0">
                                    <div className="position-relative">
                                      <a
                                        href={variation.productLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-decoration-none"
                                      >
                                        {variation.productImageUrls?.[0] || product.productImageUrls?.[0] ? (
                                          <Image
                                            src={variation.productImageUrls?.[0] || product.productImageUrls?.[0]}
                                            alt={variation.name}
                                            width={80}
                                            height={80}
                                            className="rounded"
                                            style={{ objectFit: 'cover' }}
                                            onError={(e) => {
                                              const target = e.currentTarget as HTMLImageElement;
                                              target.src = 'https://via.placeholder.com/80x80?text=No+Image';
                                            }}
                                          />
                                        ) : (
                                          <div
                                            className="rounded d-flex align-items-center justify-content-center"
                                            style={{
                                              width: '80px',
                                              height: '80px',
                                              backgroundColor: '#f8f9fa',
                                              border: '1px solid #dee2e6',
                                            }}
                                          >
                                            <Package size={20} className="text-muted" />
                                          </div>
                                        )}
                                      </a>
                                    </div>
                                  </div>

                                  {/* Variation Details */}
                                  <div className="flex-grow-1">
                                    {/* Header with badges only (no action buttons for variations) */}
                                    <div className="d-flex gap-2 flex-wrap mb-2">
                                      <Badge
                                        bg={getProductTypeColor(SkinSeoulProductType.Variation)}
                                        style={{ fontSize: '0.7rem' }}
                                      >
                                        Variation
                                      </Badge>
                                      <Badge
                                        bg={getPublicationStatusColor(variation.postStatus!)}
                                        style={{ fontSize: '0.7rem' }}
                                      >
                                        {variation.postStatus || PublicationStatus.DRAFT}
                                      </Badge>
                                      <Badge bg={getStockStatusColor(variation.status!)} style={{ fontSize: '0.7rem' }}>
                                        {variation.status === StockStatus.OUT_OF_STOCK
                                          ? 'Out of Stock'
                                          : variation.status === StockStatus.IN_STOCK
                                            ? 'In Stock'
                                            : variation.status === StockStatus.ON_BACKORDER
                                              ? 'On Backorder'
                                              : variation.status || 'Out of Stock'}
                                      </Badge>
                                    </div>

                                    {/* SKU and CP Info */}
                                    <div className="d-flex align-items-center gap-3 mb-2">
                                      <div className="d-flex align-items-center">
                                        <IdBadge type={IdBadgeType.SKU} value={variation.sku} size="sm" />
                                      </div>

                                      {variation.attributes?.['cp_parent_code'] && (
                                        <div className="d-flex align-items-center gap-2">
                                          <IdBadge
                                            type={IdBadgeType.CPPI}
                                            value={variation.attributes['cp_parent_code'] || ''}
                                            size="sm"
                                          />
                                          <IdBadge
                                            type={IdBadgeType.CPII}
                                            value={variation.attributes['cp_child_code'] || ''}
                                            size="sm"
                                          />
                                          {variation.attributes?.['cp_url'] && (
                                            <a
                                              href={variation.attributes['cp_url']}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-decoration-none"
                                              style={{ fontSize: '0.8rem' }}
                                            >
                                              View on Coupang
                                            </a>
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    {/* Variation Name */}
                                    <h6 className="fw-bold mb-1" style={{ fontSize: '0.9rem', lineHeight: '1.2' }}>
                                      {variation.name}
                                    </h6>

                                    {/* Size, Pack, Color */}
                                    <div className="d-flex gap-2 mb-2 flex-wrap">
                                      {variation['size'] && (
                                        <div className="d-flex align-items-center">
                                          <small className="text-muted me-1">Size:</small>
                                          <small>{variation['size']}</small>
                                        </div>
                                      )}
                                      {variation['pack'] && (
                                        <div className="d-flex align-items-center">
                                          <small className="text-muted me-1">Pack:</small>
                                          <small>{variation['pack']}</small>
                                        </div>
                                      )}
                                      {variation.attributes?.['color'] && (
                                        <div className="d-flex align-items-center">
                                          <small className="text-muted me-1">Color:</small>
                                          <small>{variation.attributes['color']}</small>
                                        </div>
                                      )}
                                    </div>

                                    {/* Price Information */}
                                    {(variation.originalPrice || variation.salePrice) && (
                                      <div className="d-flex align-items-center gap-2">
                                        <div className="d-flex align-items-center gap-2">
                                          {variation.salePrice ? (
                                            <>
                                              <span className="text-danger me-2">
                                                {variation.currency || 'SGD'} {variation.salePrice.toFixed(2)}
                                              </span>
                                              {variation.originalPrice && (
                                                <span
                                                  className="text-muted text-decoration-line-through me-2"
                                                  style={{ fontSize: '0.8rem' }}
                                                >
                                                  {variation.currency || 'SGD'} {variation.originalPrice.toFixed(2)}
                                                </span>
                                              )}
                                              {variation.originalPrice && (
                                                <span
                                                  className="badge bg-danger text-white"
                                                  style={{ fontSize: '0.6rem' }}
                                                >
                                                  {Math.round(
                                                    ((variation.originalPrice - variation.salePrice) /
                                                      variation.originalPrice) *
                                                      100,
                                                  )}
                                                  % OFF
                                                </span>
                                              )}
                                            </>
                                          ) : (
                                            variation.originalPrice && (
                                              <span className="text-dark" style={{ fontSize: '0.8rem' }}>
                                                {variation.currency || 'SGD'} {variation.originalPrice.toFixed(2)}
                                              </span>
                                            )
                                          )}
                                        </div>
                                        {variation.attributes?.['total_sales'] && (
                                          <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                                            ({variation.attributes['total_sales']} sold)
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </Card.Body>
                            </Card>
                          ))}
                    </React.Fragment>
                  ))}
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Image Gallery Modal */}
      {selectedProduct && (
        <Modal show={showImageModal} onHide={closeImageModal} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>{selectedProduct?.name} - Image Gallery</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="container-fluid">
              {/* Product Images */}
              {selectedProduct.productImageUrls && selectedProduct.productImageUrls.length > 0 && (
                <div>
                  <h6 className="mb-3">
                    Product Images ({Array.from(new Set(selectedProduct.productImageUrls)).length})
                  </h6>
                  <div className="d-flex flex-wrap gap-2">
                    {Array.from(new Set(selectedProduct.productImageUrls)).map((url, index) => {
                      // Extract filename from URL
                      const urlParts = url.split('/');
                      const filename = urlParts[urlParts.length - 1] || `product-image-${index + 1}`;

                      return (
                        <div key={index} className="position-relative mb-2">
                          <Image
                            src={url}
                            alt={`Product ${index + 1}`}
                            width={150}
                            height={150}
                            className="rounded"
                            style={{
                              objectFit: 'cover',
                              cursor: 'pointer',
                            }}
                            onClick={() => window.open(url, '_blank')}
                            onError={(e) => {
                              const target = e.currentTarget as HTMLImageElement;
                              target.src = 'https://via.placeholder.com/150x150?text=Image+Not+Found';
                            }}
                          />
                          <div
                            className="position-absolute top-0 end-0 m-1 px-1 text-white"
                            style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.6)',
                              fontSize: '0.7rem',
                              borderRadius: '2px',
                            }}
                          >
                            #{index + 1}
                          </div>
                          {/* Image path and filename */}
                          <div className="mt-1" style={{ width: '150px' }}>
                            <small
                              className="text-muted d-block"
                              style={{ fontSize: '0.65rem', wordBreak: 'break-all' }}
                            >
                              <strong>File:</strong> {filename}
                            </small>
                            <small
                              className="text-muted d-block"
                              style={{ fontSize: '0.6rem', wordBreak: 'break-all' }}
                            >
                              <strong>Path:</strong> {url}
                            </small>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Learn More Images */}
              {selectedProduct.learnMoreUrl && (
                <div>
                  <h6 className="mb-3">
                    Learn More Images (
                    {(() => {
                      if (selectedProduct.learnMoreUrl && Array.isArray(selectedProduct.learnMoreUrl)) {
                        return selectedProduct.learnMoreUrl.length;
                      }
                      const learnMoreUrls = selectedProduct.attributes?.['learn_more_url'];
                      if (typeof learnMoreUrls === 'string') {
                        return learnMoreUrls.split(',').length;
                      } else if (Array.isArray(learnMoreUrls)) {
                        return (learnMoreUrls as string[]).length;
                      }
                      return 0;
                    })()}
                    )
                  </h6>
                  <div className="d-flex flex-wrap gap-2">
                    {(() => {
                      let urls: string[] = [];

                      if (selectedProduct.learnMoreUrl && Array.isArray(selectedProduct.learnMoreUrl)) {
                        urls = selectedProduct.learnMoreUrl;
                      } else if (selectedProduct.attributes?.['learn_more_url']) {
                        const learnMoreUrls = selectedProduct.attributes['learn_more_url'];
                        if (typeof learnMoreUrls === 'string') {
                          urls = learnMoreUrls.split(',').map((url) => url.trim());
                        } else if (Array.isArray(learnMoreUrls)) {
                          urls = learnMoreUrls;
                        }
                      }

                      return urls.map((url, index) => {
                        // Extract filename from URL
                        const urlParts = url.split('/');
                        const filename = urlParts[urlParts.length - 1] || `pdp-image-${index + 1}`;

                        return (
                          <div key={index} className="position-relative mb-2">
                            <Image
                              src={url}
                              alt={`Learn More ${index + 1}`}
                              width={150}
                              height={150}
                              className="rounded"
                              style={{
                                objectFit: 'cover',
                                cursor: 'pointer',
                              }}
                              onClick={() => window.open(url, '_blank')}
                              onError={(e) => {
                                const target = e.currentTarget as HTMLImageElement;
                                target.src = 'https://via.placeholder.com/150x150?text=PDP+Image+Not+Found';
                              }}
                            />
                            <div
                              className="position-absolute top-0 end-0 m-1 px-1 text-white"
                              style={{
                                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                fontSize: '0.7rem',
                                borderRadius: '2px',
                              }}
                            >
                              PDP {index + 1}
                            </div>
                            {/* Image path and filename */}
                            <div className="mt-1" style={{ width: '150px' }}>
                              <small
                                className="text-muted d-block"
                                style={{ fontSize: '0.65rem', wordBreak: 'break-all' }}
                              >
                                <strong>File:</strong> {filename}
                              </small>
                              <small
                                className="text-muted d-block"
                                style={{ fontSize: '0.6rem', wordBreak: 'break-all' }}
                              >
                                <strong>Path:</strong> {url}
                              </small>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {/* No images message */}
              {(!selectedProduct.productImageUrls || selectedProduct.productImageUrls.length === 0) &&
                !selectedProduct.attributes?.['learn_more_url'] && (
                  <div className="text-center py-4 text-muted">
                    <Package size={48} className="mb-2" />
                    <p>No images available for this product</p>
                  </div>
                )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeImageModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};
